import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Launch as LaunchIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  Language as WebsiteIcon,
  CheckCircle as CompleteIcon,
  Pending as PendingIcon,
  PlayArrow as ActiveIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'viewMode'
})(({ theme, viewMode }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: viewMode === 'list' ? 'row' : 'column',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
  // Responsive adjustments for list view
  ...(viewMode === 'list' && {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  }),
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
    padding: theme.spacing(1),
  },
}));

const ResponsiveTypography = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
  },
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25),
  },
}));

const WebsiteUrl = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '200px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '150px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '120px',
  },
}));

const campaignGoalLabels = {
  'product_launch': 'Product Launch',
  'brand_awareness': 'Brand Awareness',
  'event_promotion': 'Event Promotion',
  'sales_promotion': 'Sales/Promotion',
  'content_promotion': 'Content Promotion'
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'active':
      return <ActiveIcon />;
    case 'ready':
      return <PendingIcon />;
    case 'completed':
      return <CompleteIcon />;
    case 'failed':
      return <ErrorIcon />;
    default:
      return <PendingIcon />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'ready':
      return 'warning';
    case 'completed':
      return 'info';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getProgressValue = (campaign) => {
  if (campaign.totalPosts === 0) return 0;
  return (campaign.publishedPosts / campaign.totalPosts) * 100;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const CampaignCard = ({ 
  campaign, 
  onClick, 
  viewMode = 'grid',
  onEdit,
  onSchedule,
  onAnalytics,
  onDelete 
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = () => {
    onClick(campaign);
  };

  const handleActionClick = (action, event) => {
    event.stopPropagation();
    handleMenuClose();
    
    switch (action) {
      case 'edit':
        onEdit?.(campaign);
        break;
      case 'schedule':
        onSchedule?.(campaign);
        break;
      case 'analytics':
        onAnalytics?.(campaign);
        break;
      case 'delete':
        onDelete?.(campaign);
        break;
      default:
        break;
    }
  };

  const progressValue = getProgressValue(campaign);

  if (viewMode === 'list') {
    return (
      <StyledCard viewMode={viewMode} onClick={handleCardClick}>
        <CardContent sx={{ 
          flex: 1, 
          p: { xs: 3, sm: 3 },
          pb: { xs: 2, sm: 3 }
        }}>
          {/* Mobile Layout */}
          <Box sx={{ 
            display: { xs: 'block', md: 'flex' },
            alignItems: { md: 'center' },
            gap: { md: 2 }
          }}>
            {/* Header Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 2,
              mb: { xs: 2.5, md: 0 },
              flex: { md: 1 }
            }}>
              <Avatar
                sx={{
                  bgcolor: getStatusColor(campaign.status) + '.main',
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  flexShrink: 0
                }}
              >
                {getStatusIcon(campaign.status)}
              </Avatar>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    lineHeight: 1.3,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {campaign.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <WebsiteIcon fontSize="small" color="action" />
                  <WebsiteUrl 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                  >
                    {campaign.websiteUrl}
                  </WebsiteUrl>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={campaignGoalLabels[campaign.campaignGoal] || campaign.campaignGoal}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      height: 28
                    }}
                  />
                  <Chip
                    label={campaign.status}
                    size="small"
                    color={getStatusColor(campaign.status)}
                    sx={{ 
                      fontSize: '0.75rem',
                      height: 28,
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>

              {/* Desktop Menu Button */}
              <IconButton 
                onClick={handleMenuClick} 
                size="small"
                sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  mt: 0.5
                }}
              >
                <MoreIcon />
              </IconButton>
            </Box>

            {/* Stats Section - Redesigned for Mobile */}
            <Box sx={{ 
              display: { xs: 'block', md: 'flex' },
              gap: { md: 3 },
              minWidth: { md: 400 }
            }}>
              {/* Progress Section - Mobile First */}
              <Box sx={{ 
                mb: { xs: 2, md: 0 },
                order: { md: 2 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 1 
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Campaign Progress
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.primary"
                    sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    {Math.round(progressValue)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progressValue}
                  color={progressValue === 100 ? 'success' : 'primary'}
                  sx={{ 
                    height: { xs: 8, sm: 6 },
                    borderRadius: 1,
                    bgcolor: 'grey.100'
                  }}
                />
              </Box>

              {/* Stats Grid - Mobile Optimized */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(3, minmax(80px, 1fr))' },
                gap: { xs: 2, md: 2 },
                order: { md: 1 }
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      lineHeight: 1,
                      color: 'text.primary'
                    }}
                  >
                    {campaign.totalPosts || 0}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.75rem' },
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    Total
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    color="warning.main"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      lineHeight: 1
                    }}
                  >
                    {campaign.scheduledPosts || 0}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.75rem' },
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    Scheduled
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    color="success.main"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      lineHeight: 1
                    }}
                  >
                    {campaign.publishedPosts || 0}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.75rem' },
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    Published
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Mobile Action Bar */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              Created {formatDate(campaign.createdAt)}
            </Typography>
            <IconButton 
              onClick={handleMenuClick} 
              size="small"
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard viewMode={viewMode} onClick={handleCardClick}>
      <CardContent sx={{ 
        flexGrow: 1,
        p: { xs: 2, sm: 3 },
        '&:last-child': { pb: { xs: 2, sm: 3 } }
      }}>
        <CardHeader>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ResponsiveTypography 
              variant="h6" 
              component="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {campaign.name}
            </ResponsiveTypography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <WebsiteIcon fontSize="small" color="action" />
              <WebsiteUrl variant="body2" color="text.secondary">
                {campaign.websiteUrl}
              </WebsiteUrl>
            </Box>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleMenuClick}
            sx={{ flexShrink: 0 }}
          >
            <MoreIcon />
          </IconButton>
        </CardHeader>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={campaignGoalLabels[campaign.campaignGoal] || campaign.campaignGoal}
            size="small"
            variant="outlined"
            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
          />
          <Chip
            label={campaign.status}
            size="small"
            color={getStatusColor(campaign.status)}
            icon={getStatusIcon(campaign.status)}
            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
          />
        </Box>

        {campaign.description && (
          <ResponsiveTypography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: { xs: 3, sm: 2 },
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.4,
            }}
          >
            {campaign.description}
          </ResponsiveTypography>
        )}

        <StatsContainer>
          <StatBox>
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {campaign.totalPosts || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Posts
            </Typography>
          </StatBox>
          <StatBox>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              color="warning.main"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {campaign.scheduledPosts || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Scheduled
            </Typography>
          </StatBox>
          <StatBox>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              color="success.main"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {campaign.publishedPosts || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Published
            </Typography>
          </StatBox>
        </StatsContainer>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              {Math.round(progressValue)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progressValue}
            color={progressValue === 100 ? 'success' : 'primary'}
            sx={{ 
              height: { xs: 6, sm: 4 },
              borderRadius: 1,
            }}
          />
        </Box>
      </CardContent>

      <CardActions sx={{ 
        justifyContent: 'space-between', 
        px: { xs: 2, sm: 3 }, 
        pb: { xs: 2, sm: 3 },
        pt: 0,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: 1
      }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            order: { xs: 2, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Created {formatDate(campaign.createdAt)}
        </Typography>
        <Button 
          size="small" 
          endIcon={<LaunchIcon />}
          onClick={handleCardClick}
          sx={{ 
            order: { xs: 1, sm: 2 },
            minWidth: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}
        >
          View
        </Button>
      </CardActions>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={(e) => handleActionClick('edit', e)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Campaign
        </MenuItem>
        <MenuItem onClick={(e) => handleActionClick('schedule', e)}>
          <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
          Schedule Posts
        </MenuItem>
        <MenuItem onClick={(e) => handleActionClick('analytics', e)}>
          <TrendingIcon sx={{ mr: 1 }} fontSize="small" />
          View Analytics
        </MenuItem>
        <MenuItem 
          onClick={(e) => handleActionClick('delete', e)}
          sx={{ color: 'error.main' }}
        >
          <ErrorIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Campaign
        </MenuItem>
      </Menu>
    </StyledCard>
  );
};

export default CampaignCard; 